import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";

import { AuthProvider } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import PortalLayout from "@/components/PortalLayout";
import AdminLayout from "@/components/AdminLayout";
import ProtectedRoute from "@/components/ProtectedRoute";

// Public pages
import HomePage from "@/pages/Home";
import AboutPage from "@/pages/About";
import ServicesPage from "@/pages/Services";
import ServiceDetailPage from "@/pages/ServiceDetail";
import BookPage from "@/pages/Book";
import IntakePage from "@/pages/Intake";
import FAQPage from "@/pages/FAQ";
import BlogPage from "@/pages/Blog";
import BlogDetailPage from "@/pages/BlogDetail";
import ContactPage from "@/pages/Contact";
import HelpMeChoosePage from "@/pages/HelpMeChoose";
import NotFoundPage from "@/pages/NotFound";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";

// Auth
import ClientLogin from "@/pages/ClientLogin";
import TeamLogin from "@/pages/TeamLogin";
import AuthCallback from "@/pages/AuthCallback";

// Portal
import PortalDashboard from "@/pages/portal/PortalDashboard";
import PortalServices from "@/pages/portal/PortalServices";
import PortalAppointments from "@/pages/portal/PortalAppointments";
import PortalInvoices from "@/pages/portal/PortalInvoices";
import PortalDocuments from "@/pages/portal/PortalDocuments";
import PortalMessages from "@/pages/portal/PortalMessages";
import PortalCreditRepair from "@/pages/portal/PortalCreditRepair";

// Admin
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminClients from "@/pages/admin/AdminClients";
import AdminClientDetail from "@/pages/admin/AdminClientDetail";
import AdminAppointments from "@/pages/admin/AdminAppointments";
import AdminInvoices from "@/pages/admin/AdminInvoices";
import AdminAnnouncements from "@/pages/admin/AdminAnnouncements";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminDrips from "@/pages/admin/AdminDrips";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminProfile from "@/pages/admin/AdminProfile";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Detect Emergent OAuth callback synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public site */}
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/:slug" element={<ServiceDetailPage />} />
          <Route path="/book" element={<BookPage />} />
          <Route path="/intake" element={<IntakePage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/insights" element={<BlogPage />} />
          <Route path="/insights/:slug" element={<BlogDetailPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/help-me-choose" element={<HelpMeChoosePage />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Auth */}
        <Route path="/login" element={<ClientLogin />} />
        <Route path="/team/login" element={<TeamLogin />} />
        <Route path="/profile" element={<AuthCallback />} />

        {/* Client Portal */}
        <Route
          element={
            <ProtectedRoute roles={["client"]}>
              <PortalLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/portal" element={<PortalDashboard />} />
          <Route path="/portal/services" element={<PortalServices />} />
          <Route path="/portal/credit-repair" element={<PortalCreditRepair />} />
          <Route path="/portal/appointments" element={<PortalAppointments />} />
          <Route path="/portal/invoices" element={<PortalInvoices />} />
          <Route path="/portal/documents" element={<PortalDocuments />} />
          <Route path="/portal/messages" element={<PortalMessages />} />
        </Route>

        {/* Admin / Team */}
        <Route
          element={
            <ProtectedRoute roles={["admin", "team_member"]}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/admin" element={<AdminOverview />} />
          <Route path="/admin/leads" element={<AdminLeads />} />
          <Route path="/admin/clients" element={<AdminClients />} />
          <Route path="/admin/clients/:user_id" element={<AdminClientDetail />} />
          <Route path="/admin/appointments" element={<AdminAppointments />} />
          <Route path="/admin/invoices" element={<AdminInvoices />} />
          <Route path="/admin/announcements" element={<AdminAnnouncements />} />
          <Route path="/admin/drips" element={<AdminDrips />} />
          <Route path="/admin/integrations" element={<AdminIntegrations />} />
          <Route path="/admin/reviews" element={<AdminReviews />} />
          <Route path="/admin/messages" element={<AdminMessages />} />
          <Route path="/admin/profile" element={<AdminProfile />} />
          <Route
            path="/admin/team"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminTeam />
              </ProtectedRoute>
            }
          />
        </Route>
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
